import { type ReactNode, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { keepPreviousData } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { subDays } from 'date-fns';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import Button from '@/shared/components/flip-ui-kit/Button';
import BoostPoolLogo from '@/shared/components/logo/BoostPoolLogo';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { type AllBoostBalancesForAccountQuery } from '@/shared/graphql/generated/cache/graphql';
import { getEarnedFeesByPool24hsForAccount } from '@/shared/graphql/lp/boost';
import { useAllBoostPools } from '@/shared/hooks/useAllBoostPools';
import { useBoostPoolApy } from '@/shared/hooks/useBoostPoolApy';
import useBoostPoolId from '@/shared/hooks/useBoostPoolId';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useInterval from '@/shared/hooks/useInterval';
import useTableSort from '@/shared/hooks/useTableSort';
import useTracking from '@/shared/hooks/useTracking';
import { SharedEvents, type SharedTrackEvents } from '@/shared/types';
import { apyToText, getBoostPoolId } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import { COMPARATORS } from '@/shared/utils/helpers';
import { formatUsdValue, pipsToPercentString } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type PositionRowProps = {
  asset: ChainflipAsset;
  feeTierPips: number;
  poolShare: number;
  availableAmount: string;
  availableAmountValueUsd: string;
  unavailableAmount: string;
  unavailableAmountValueUsd: string;
  apy?: number;
  fee: string;
  feeUsd: string;
  readonly?: boolean;
  onRowClick?: (asset: ChainflipAsset, feeTierPips: number) => void;
  accountIdSs58?: string;
  action?: ReactNode;
};

const PositionRow = ({
  asset,
  feeTierPips,
  poolShare,
  availableAmount,
  availableAmountValueUsd,
  unavailableAmount,
  unavailableAmountValueUsd,
  apy,
  fee,
  feeUsd,
  readonly,
  onRowClick,
  accountIdSs58,
  action,
}: PositionRowProps) => {
  const track = useTracking<SharedTrackEvents>();
  const router = useRouter();
  const balance = BigNumber.sum(availableAmount, unavailableAmount);
  const amount = TokenAmount.fromAsset(balance, asset);
  const usd = BigNumber.sum(0, availableAmountValueUsd, unavailableAmountValueUsd);
  const feeAmount = TokenAmount.fromAsset(fee, asset);

  return (
    <ClickableTableRow
      disableHoverStyle={!onRowClick}
      className={classNames(onRowClick && 'h-[64px]')}
      onClick={() => onRowClick?.(asset, feeTierPips)}
    >
      <td>
        <div className="flex flex-col gap-y-1">
          <div className="flex items-center gap-x-2 font-aeonikMedium text-cf-white">
            <BoostPoolLogo />
            <div>
              <div className="font-aeonikBold">
                <span className="text-cf-white">BTC</span>
                <span className="text-cf-white"> {pipsToPercentString(feeTierPips)}</span>
              </div>
              <div className="font-aeonikRegular text-cf-light-2">Bitcoin (Boost)</div>
            </div>
          </div>
        </div>
      </td>
      <td className="font-aeonikMedium text-20 text-cf-white">{apyToText(apy)}</td>
      <td className="font-aeonikMedium">
        <div className="flex flex-col">
          <span className="text-cf-white">
            {amount.toFormatted()} {chainflipAssetMap[asset].symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(usd)}</span>
        </div>
      </td>
      <td className="font-aeonikMedium">
        <div className="flex flex-col">
          <span className="flex gap-x-1 text-cf-white">
            {feeAmount.toFormatted()} {chainflipAssetMap[asset].symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(feeUsd)}</span>
        </div>
      </td>
      <td
        className={classNames(
          'font-aeonikMedium text-cf-white',
          readonly && !action && 'text-right',
        )}
      >
        {poolShare.toFixed(2)}%
      </td>
      {action && <td>{action}</td>}
      {!readonly && !action && (
        <td className="text-right">
          <div className="flex justify-end">
            {amount.eq(0) || usd.eq(0) ? (
              <Button
                type="primary-standard"
                onClick={(e) => {
                  e.stopPropagation();
                  track(SharedEvents.AddBoostLiquidity, {
                    props: {
                      path: window.location.pathname,
                      scAccount: accountIdSs58!,
                      feeTierBps: feeTierPips,
                      position: 'my_boost_positions',
                    },
                  });
                  router.push(`/boost/pools/${asset}/${feeTierPips}?tab=me`);
                }}
              >
                Add Liquidity
              </Button>
            ) : (
              <Button
                type="primary-standard"
                onClick={(e) => {
                  e.stopPropagation();
                  router.push(`/boost/pools/${asset}/${feeTierPips}?tab=me`);
                }}
              >
                Manage
              </Button>
            )}
          </div>
        </td>
      )}
    </ClickableTableRow>
  );
};
const DEFAULT_SORT = { col: 1, direction: 'DESC' as 'ASC' | 'DESC' };

export const PositionsTable = ({
  boostBalances,
  boostBalancesLoading,
  readonly = false,
  showOnlyMyPositions = false,
  accountIdSs58,
  className,
  onRowClick,
  renderAction,
  sortable = true,
}: {
  boostBalances: NonNullable<AllBoostBalancesForAccountQuery['balances']>['nodes'];
  boostBalancesLoading?: boolean;
  readonly?: boolean;
  showOnlyMyPositions?: boolean;
  accountIdSs58: string;
  className?: string;
  onRowClick?: (asset: ChainflipAsset, feeTierPips: number) => void;
  renderAction?: (asset: ChainflipAsset, feeTierPips: number) => ReactNode;
  sortable?: boolean;
}) => {
  const { apys } = useBoostPoolApy();
  const { boostPoolIds } = useBoostPoolId({ asset: 'Btc', activeOnly: true });

  const { boostPools: cachedBoostPools } = useAllBoostPools();
  const { setCol, sort } = useTableSort({
    initialSort: DEFAULT_SORT,
  });
  const [date, setDate] = useState(subDays(new Date(), 1).toISOString());

  useInterval(() => {
    setDate(subDays(new Date(), 1).toISOString());
  }, 30_000);

  const { data: fees24Hours, isLoading: feesLoading } = useGqlQuery(
    getEarnedFeesByPool24hsForAccount,
    {
      variables: {
        accountId: accountIdSs58,
        timestamp: date,
      },
      context: { clientName: 'lpProcessor' },
      refetchInterval: 30_000,
      placeholderData: keepPreviousData, // keep data during update when date variable is changed
    },
  );

  const feesByPool = useMemo(
    () =>
      Object.fromEntries(
        (fees24Hours?.account?.boostShares.groupedAggregates ?? []).map((agg) => [
          agg.keys![0],
          agg.sum!,
        ]),
      ),
    [fees24Hours],
  );

  const positions = useMemo(
    () =>
      (readonly || showOnlyMyPositions
        ? // Only show the pools that the user has a balance in (if readonly)
          boostBalances
        : // Show all pools so the user can add liquidity (if not readonly)
          Object.entries(boostPoolIds ?? []).map(([boostPoolId, nodeId]) => {
            const [asset, feeTierPipsString] = boostPoolId.split('-');
            const feeTierPips = Number(feeTierPipsString);
            const data = boostBalances.find(
              (balanceData) =>
                balanceData.pool.asset === asset && balanceData.pool.feeTierPips === feeTierPips,
            );

            return {
              ...data,
              pool: {
                id: nodeId,
                asset: asset as ChainflipAsset,
                feeTierPips,
              },
            };
          })
      ).map(({ pool, ...data }) => {
        const apy = apys?.find(
          (a) => a.asset === pool.asset && a.feeTierPips === pool.feeTierPips,
        )?.apy;
        const boostPoolData = cachedBoostPools?.find(
          ({ asset, feeTierPips }) => asset === pool.asset && feeTierPips === pool.feeTierPips,
        );
        return {
          ...data,
          ...pool,
          boostPoolId: getBoostPoolId(pool),
          availableAmount: data?.availableAmount ?? '0',
          unavailableAmount: data?.unavailableAmount ?? '0',
          availableAmountValueUsd: data?.availableAmountValueUsd ?? '0',
          unavailableAmountValueUsd: data?.unavailableAmountValueUsd ?? '0',
          boostPoolAvailableAmount: boostPoolData?.availableAmount ?? '0',
          boostPoolUnavailableAmount: boostPoolData?.unavailableAmount ?? '0',
          boostPoolAvailableAmountUsd: boostPoolData?.availableAmountValueUsd ?? '0',
          boostPoolUnavailableAmountUsd: boostPoolData?.unavailableAmountValueUsd ?? '0',
          poolShare: data?.poolShare ?? 0,
          fee: feesByPool[pool.id]?.fee ?? '0',
          feeUsd: feesByPool[pool.id]?.feeUsd ?? '0',
          apy,
        };
      }),
    [readonly, boostBalances, boostPoolIds, fees24Hours, cachedBoostPools, apys],
  );

  const SORT_FUNCTIONS: ((props: PositionRowProps) => number)[] = [
    () => 0, // no sort
    (props) => Number(props.availableAmountValueUsd) + Number(props.unavailableAmountValueUsd),
    (props) => Number(props.feeUsd),
    () => 0,
    (props) => props.poolShare,
  ];

  const sortedRows = useMemo(
    () =>
      positions
        .slice()
        .sort((a, b) =>
          COMPARATORS[sort.direction](SORT_FUNCTIONS[sort.col](a), SORT_FUNCTIONS[sort.col](b)),
        )
        .map((data) => (
          <PositionRow
            key={data.boostPoolId}
            onRowClick={onRowClick}
            readonly={readonly}
            accountIdSs58={accountIdSs58}
            action={renderAction?.(data.asset, data.feeTierPips)}
            {...data}
          />
        )),
    [positions, sort, cachedBoostPools],
  );

  return (
    <TableV2
      isLoading={feesLoading || boostBalancesLoading}
      className={className}
      thClassName="px-1 [&:first-child]:pl-4 [&:last-child]:pr-4"
      tbodyClassName="[&>tr>td:first-child]:pl-4 [&>tr>td:last-child]:pr-4 [&>tr>td]:px-1 [&>tr>td]:py-4"
      columns={[
        { name: 'Pool', sortable: false },
        { name: 'APY', sortable: false },
        { name: 'Liquidity', sortable },
        { name: 'Earned Fees (24H)', sortable },
        { name: 'Pool Share', alignment: readonly && !renderAction ? 'right' : 'left', sortable },
        ...(readonly && !renderAction ? [] : [{ name: '', sortable: false } as const]),
      ]}
      rows={sortedRows}
      sorting={{ setCol, sort }}
    />
  );
};
